import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Plus, Edit2, Trash2, ChevronDown, ChevronUp,
    Search, Eye, EyeOff, MoreVertical, GripVertical, Smartphone, Printer, Lock, WifiOff, ShieldCheck
} from 'lucide-react';

import QRCode from 'qrcode';
import { formatPrice } from '../../lib/mockData';
import Modal from '../../components/ui/Modal';
import { api } from '../../lib/api';
import { persistence } from '../../lib/persistence';

import { useAuthStore } from '../../store/authStore';
import { resolveAssetUrl } from '../../lib/assets';
import type { ProductOptionGroup, ProductOptionSelectionRule } from '../../types';

type ProductGroupRuleKey = 'size' | 'simple_addons' | 'gourmet_addons' | 'fruits' | 'creams' | 'extras' | 'custom';
type AcaiPresetType = 'tradicional' | 'tropical' | 'puro';

type ProductOptionGroupDraft = ProductOptionGroup & {
    ruleKey?: string;
    items: Array<{
        id: string;
        name: string;
        price: number;
        available: boolean;
        selectionRules?: ProductOptionSelectionRule[];
    }>;
};

const GROUP_RULE_TYPE_OPTIONS: Array<{ value: ProductGroupRuleKey; label: string; description: string }> = [
    { value: 'custom', label: 'Grupo comum', description: 'Use para opcionais sem regra especial.' },
    { value: 'size', label: 'Tamanho / volume', description: 'Controla preco final e beneficios de outros grupos.' },
    { value: 'simple_addons', label: 'Acrescimos simples', description: 'Ex.: granola, leite em po, paçoca.' },
    { value: 'gourmet_addons', label: 'Acrescimos gourmet', description: 'Ex.: Oreo, KitKat, Nutella premium.' },
    { value: 'fruits', label: 'Frutas', description: 'Grupos de frutas liberadas conforme o tamanho.' },
    { value: 'creams', label: 'Cremes', description: 'Nutella, creme de ninho, cupuaçu e similares.' },
    { value: 'extras', label: 'Extras gerais', description: 'Use quando nao se encaixar nas opcoes acima.' },
];

const GROUP_RULE_KEY_LABELS: Record<string, string> = {
    size: 'Tamanho / volume',
    simple_addons: 'Acrescimos simples',
    gourmet_addons: 'Acrescimos gourmet',
    fruits: 'Frutas',
    creams: 'Cremes',
    extras: 'Extras gerais',
};

const priceInputFormatter = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const parsePriceInputValue = (value: string): number => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return 0;
    return Number(digits) / 100;
};

const formatPriceInputValue = (value: number): string => priceInputFormatter.format(Number(value) || 0);

const formatFileSize = (bytes?: number | null) => {
    if (!bytes || bytes <= 0) return '';
    if (bytes >= 1024 * 1024) {
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const createDraftGroup = (
    name: string,
    ruleKey: string | undefined,
    required: boolean,
    maxSelect: number,
    pricingMode: 'EXTRA' | 'ABSOLUTE',
    items: Array<{ name: string; price?: number; available?: boolean; selectionRules?: ProductOptionSelectionRule[] }>,
): ProductOptionGroupDraft => ({
    id: `preset-group-${name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name,
    ruleKey,
    required,
    maxSelect,
    pricingMode,
    items: items.map((item, index) => ({
        id: `preset-item-${name}-${index}-${Math.random().toString(36).slice(2, 7)}`,
        name: item.name,
        price: item.price || 0,
        available: item.available !== false,
        selectionRules: item.selectionRules || [],
    })),
});

const buildPizzaOptionPreset = (): ProductOptionGroupDraft[] => [
    createDraftGroup('Tamanho', 'size', true, 1, 'ABSOLUTE', [
        { name: 'Pequena', price: 32 },
        { name: 'Media', price: 40 },
        { name: 'Grande', price: 48 },
    ]),
    createDraftGroup('Borda recheada', 'extras', false, 1, 'EXTRA', [
        { name: 'Sem borda recheada', price: 0 },
        { name: 'Cheddar', price: 6 },
        { name: 'Catupiry', price: 6 },
        { name: 'Chocolate', price: 7 },
    ]),
    createDraftGroup('Extras', 'extras', false, 3, 'EXTRA', [
        { name: 'Bacon extra', price: 5 },
        { name: 'Queijo extra', price: 4 },
        { name: 'Azeitona extra', price: 3 },
    ]),
];

const buildAcaiOptionPreset = (preset: AcaiPresetType = 'tradicional'): ProductOptionGroupDraft[] => {
    const createRule = (
        targetGroupKey: ProductGroupRuleKey,
        includedQuantity: number,
        maxSelectOverride: number,
        visible = true,
    ): ProductOptionSelectionRule => ({
        targetGroupKey,
        includedQuantity,
        maxSelectOverride,
        visible,
    });

    const createSizeItem = (
        name: string,
        price: number,
        selectionRules: ProductOptionSelectionRule[],
    ): ProductOptionGroupDraft['items'][number] => ({
        id: `draft_${Math.random().toString(36).slice(2, 10)}`,
        name,
        price,
        available: true,
        selectionRules,
    });

    const sizeItemsByPreset: Record<AcaiPresetType, ProductOptionGroupDraft['items']> = {
        tradicional: [
            createSizeItem('300ml', 13, [
                    createRule('simple_addons', 1, 1),
                    createRule('gourmet_addons', 0, 2),
                    createRule('fruits', 0, 2),
                    createRule('creams', 0, 1),
                ]),
            createSizeItem('500ml', 16, [
                    createRule('simple_addons', 2, 2),
                    createRule('gourmet_addons', 0, 2),
                    createRule('fruits', 0, 2),
                    createRule('creams', 0, 1),
                ]),
            createSizeItem('700ml', 21, [
                    createRule('simple_addons', 2, 3),
                    createRule('gourmet_addons', 0, 3),
                    createRule('fruits', 0, 3),
                    createRule('creams', 0, 2),
                ]),
        ],
        tropical: [
            createSizeItem('300ml', 16, [
                    createRule('simple_addons', 0, 0, false),
                    createRule('gourmet_addons', 0, 0, false),
                    createRule('fruits', 1, 1),
                    createRule('creams', 0, 0, false),
                ]),
            createSizeItem('500ml', 18, [
                    createRule('simple_addons', 0, 0, false),
                    createRule('gourmet_addons', 0, 0, false),
                    createRule('fruits', 2, 2),
                    createRule('creams', 0, 0, false),
                ]),
            createSizeItem('700ml', 21, [
                    createRule('simple_addons', 0, 0, false),
                    createRule('gourmet_addons', 0, 0, false),
                    createRule('fruits', 3, 3),
                    createRule('creams', 0, 0, false),
                ]),
        ],
        puro: [
            createSizeItem('300ml', 11, [
                    createRule('simple_addons', 0, 0, false),
                    createRule('gourmet_addons', 0, 0, false),
                    createRule('fruits', 0, 0, false),
                    createRule('creams', 0, 0, false),
                ]),
            createSizeItem('500ml', 14, [
                    createRule('simple_addons', 0, 0, false),
                    createRule('gourmet_addons', 0, 0, false),
                    createRule('fruits', 0, 0, false),
                    createRule('creams', 0, 0, false),
                ]),
            createSizeItem('700ml', 17, [
                    createRule('simple_addons', 0, 0, false),
                    createRule('gourmet_addons', 0, 0, false),
                    createRule('fruits', 0, 0, false),
                    createRule('creams', 0, 0, false),
                ]),
        ],
    };

    const groupsByPreset: Record<AcaiPresetType, ProductOptionGroupDraft[]> = {
        tradicional: [
    createDraftGroup('Tamanho', 'size', true, 1, 'ABSOLUTE', [
        {
            name: '300ml',
            price: 13,
            selectionRules: [
                { targetGroupKey: 'simple_addons', includedQuantity: 1, maxSelectOverride: 1, visible: true },
                { targetGroupKey: 'fruits', includedQuantity: 0, maxSelectOverride: 0, visible: false },
            ],
        },
        {
            name: '500ml',
            price: 16,
            selectionRules: [
                { targetGroupKey: 'simple_addons', includedQuantity: 2, maxSelectOverride: 2, visible: true },
                { targetGroupKey: 'fruits', includedQuantity: 0, maxSelectOverride: 0, visible: false },
            ],
        },
        {
            name: '700ml',
            price: 21,
            selectionRules: [
                { targetGroupKey: 'simple_addons', includedQuantity: 2, maxSelectOverride: 3, visible: true },
                { targetGroupKey: 'fruits', includedQuantity: 0, maxSelectOverride: 0, visible: false },
            ],
        },
    ]),
    createDraftGroup('Acrescimos simples', 'simple_addons', false, 3, 'EXTRA', [
        { name: 'Granola', price: 3 },
        { name: 'Leite em po', price: 3 },
        { name: 'Leite condensado', price: 3 },
        { name: 'Paçoca', price: 3 },
        { name: 'Banana', price: 3 },
    ]),
    createDraftGroup('Acrescimos gourmet', 'gourmet_addons', false, 3, 'EXTRA', [
        { name: 'Oreo', price: 4 },
        { name: 'KitKat', price: 4 },
        { name: 'M&M', price: 4 },
        { name: 'Ovomaltine', price: 4 },
    ]),
    createDraftGroup('Frutas', 'fruits', false, 3, 'EXTRA', [
        { name: 'Morango', price: 2 },
        { name: 'Banana', price: 2 },
        { name: 'Kiwi', price: 3 },
        { name: 'Manga', price: 3 },
    ]),
    createDraftGroup('Cremes', 'creams', false, 2, 'EXTRA', [
        { name: 'Nutella', price: 7 },
        { name: 'Creme de ninho', price: 7 },
        { name: 'Creme de morango', price: 7 },
    ]),
        ],
        tropical: [
            createDraftGroup('Frutas', 'fruits', false, 3, 'EXTRA', [
                { name: 'Morango', price: 2 },
                { name: 'Banana', price: 2 },
                { name: 'Manga', price: 3 },
                { name: 'Kiwi', price: 3 },
            ]),
        ],
        puro: [],
    };

    void groupsByPreset;

    const resolvedGroupsByPreset: Record<AcaiPresetType, ProductOptionGroupDraft[]> = {
        tradicional: [
            createDraftGroup('Acrescimos simples', 'simple_addons', false, 3, 'EXTRA', [
                { name: 'Granola', price: 3 },
                { name: 'Leite em po', price: 3 },
                { name: 'Leite condensado', price: 3 },
                { name: 'Pacoquinha', price: 3 },
                { name: 'Banana', price: 3 },
            ]),
            createDraftGroup('Acrescimos gourmet', 'gourmet_addons', false, 3, 'EXTRA', [
                { name: 'Oreo', price: 4 },
                { name: 'KitKat', price: 4 },
                { name: 'M&M', price: 4 },
                { name: 'Ovomaltine', price: 4 },
            ]),
            createDraftGroup('Frutas', 'fruits', false, 3, 'EXTRA', [
                { name: 'Morango', price: 2 },
                { name: 'Banana', price: 2 },
                { name: 'Kiwi', price: 3 },
                { name: 'Manga', price: 3 },
            ]),
            createDraftGroup('Cremes', 'creams', false, 2, 'EXTRA', [
                { name: 'Nutella', price: 7 },
                { name: 'Creme de ninho', price: 7 },
                { name: 'Creme de morango', price: 7 },
            ]),
        ],
        tropical: [
            createDraftGroup('Frutas', 'fruits', false, 3, 'EXTRA', [
                { name: 'Morango', price: 2 },
                { name: 'Banana', price: 2 },
                { name: 'Manga', price: 3 },
                { name: 'Kiwi', price: 3 },
                { name: 'Uva', price: 3 },
            ]),
        ],
        puro: [],
    };

    return [
        createDraftGroup('Tamanho', 'size', true, 1, 'ABSOLUTE', sizeItemsByPreset[preset]),
        ...resolvedGroupsByPreset[preset],
    ];
};

const getNormalizedRuleKey = (value?: string) => (value && value !== 'custom' ? value : undefined);

const getSelectionRuleForGroup = (item: ProductOptionGroupDraft['items'][number], targetGroupKey: string) =>
    (item.selectionRules || []).find((rule) => rule.targetGroupKey === targetGroupKey);

const getProductDisplayPrice = (product: { price: number; optionGroups?: ProductOptionGroup[] }) => {
    const absolutePrices = (product.optionGroups || [])
        .filter((group) => group.pricingMode === 'ABSOLUTE')
        .flatMap((group) => (group.items || []).filter((item) => item.available !== false).map((item) => item.price))
        .filter((price) => Number.isFinite(price));

    if (absolutePrices.length > 0) {
        return Math.min(...absolutePrices);
    }

    return product.price;
};

const AdminMenu: React.FC = () => {
    const { user } = useAuthStore();
    const canManageMenu = user?.role === 'owner';
    const [categories, setCategories] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCats, setExpandedCats] = useState<string[]>([]);
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [establishmentSlug, setEstablishmentSlug] = useState('');
    const [arQrCodeDataUrl, setArQrCodeDataUrl] = useState('');
    const [selectedPreviewArProduct, setSelectedPreviewArProduct] = useState<any | null>(null);
    const [productOptionGroups, setProductOptionGroups] = useState<ProductOptionGroupDraft[]>([]);
    const [threeDModelFeatureEnabled, setThreeDModelFeatureEnabled] = useState(true);
    const [threeDModelAccessHint, setThreeDModelAccessHint] = useState('');
    const [selected3dModelFile, setSelected3dModelFile] = useState<File | null>(null);
    const [selected3dUsdzFile, setSelected3dUsdzFile] = useState<File | null>(null);
    const [remove3dModelOnSave, setRemove3dModelOnSave] = useState(false);
    const [model3dNotice, setModel3dNotice] = useState('');
    const [productSaving, setProductSaving] = useState(false);
    const [categorySaving, setCategorySaving] = useState(false);
    const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
    const [deletingCategoryId, setDeletingCategoryId] = useState<string | null>(null);
    const [togglingProductId, setTogglingProductId] = useState<string | null>(null);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [lastSync, setLastSync] = useState<string | null>(persistence.getLastSync());


    const loadData = useCallback(async () => {
        if (!user?.establishmentId) {
            setLoadError('Nao foi possivel identificar o restaurante da sua conta.');
            setIsLoading(false);
            return;
        }
        try {
            setIsLoading(true);
            setLoadError('');
            const [{ data: categoriesData }, { data: establishmentData }] = await Promise.all([
                api.get(`/categories/establishment/${user.establishmentId}`),
                api.get(`/establishments/id/${user.establishmentId}`),
            ]);
            const featureEnabled = establishmentData.settings?.has3dModelModule === true;

            setCategories(categoriesData);
            setEstablishmentSlug(establishmentData.slug || '');
            setExpandedCats(categoriesData.map((category: any) => category.id));
            setThreeDModelFeatureEnabled(featureEnabled);
            setThreeDModelAccessHint(
                featureEnabled
                    ? 'Envie um .glb para o 3D web e, se quiser AR nativo no iPhone, adicione tambem um .usdz.'
                    : 'Recurso liberado pela plataforma por feature flag para este restaurante.',
            );

            // Persistir para segurança offline
            persistence.save('MENU', categoriesData);
            setLastSync(new Date().toISOString());
            setIsOffline(false);
        } catch (error: any) {
            console.error(error);
            setIsOffline(true);


            setLoadError(
                error?.response?.data?.message
                || 'Nao foi possivel carregar o cardapio. Exibindo versao salva localmente.'
            );
        } finally {
            setIsLoading(false);
        }


    }, [user?.establishmentId]);

    useEffect(() => {
        // Carga inicial do cache (segurança offline)
        const cachedMenu = persistence.load<any[]>('MENU');
        if (cachedMenu) {
            setCategories(cachedMenu);
            setExpandedCats(cachedMenu.map((category: any) => category.id));
        }

        loadData();

        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadData]);



    const filteredCategories = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return categories
            .map((category) => ({
                ...category,
                products: (category.products || []).filter((product: any) => {
                    if (!term) return true;
                    return (
                        category.name.toLowerCase().includes(term)
                        || product.name.toLowerCase().includes(term)
                        || product.description?.toLowerCase().includes(term)
                    );
                }),
            }))
            .filter((category) => {
                if (!term) return true;
                return category.name.toLowerCase().includes(term) || category.products.length > 0;
            });
    }, [categories, searchTerm]);

    const selectedProductCategoryName = useMemo(() => {
        if (!selectedProduct?.categoryId) return '';
        return categories.find((category) => category.id === selectedProduct.categoryId)?.name?.toLowerCase() || '';
    }, [categories, selectedProduct?.categoryId]);

    const isPizzaProductFlow = selectedProductCategoryName.includes('pizza');
    const isAcaiProductFlow = selectedProductCategoryName.includes('acai') || selectedProductCategoryName.includes('açaí');
    const usesAbsolutePricing = productOptionGroups.some((group) => group.pricingMode === 'ABSOLUTE');
    const shouldHideBasePrice = isPizzaProductFlow || usesAbsolutePricing;
    const firstArProduct = useMemo(
        () => categories.flatMap((category) => category.products || []).find((product: any) => product.model3dEnabled && product.model3dGlbUrl),
        [categories],
    );
    const activeArProduct = selectedPreviewArProduct || firstArProduct;
      const arPublicUrl = useMemo(() => {
          if (!establishmentSlug) return '';

        const search = new URLSearchParams();
        if (activeArProduct?.id) {
            search.set('item', activeArProduct.id);
        }

        return search.toString()
            ? `${window.location.origin}/store/${establishmentSlug}/ar?${search.toString()}`
            : `${window.location.origin}/store/${establishmentSlug}/ar`;
      }, [activeArProduct?.id, establishmentSlug]);
    const qrManagementUrl = useMemo(
        () => establishmentSlug ? `${window.location.origin}/store/${establishmentSlug}/qr` : '',
        [establishmentSlug],
    );
    const targetableGroupOptions = useMemo(
        () => productOptionGroups
            .filter((group) => group.ruleKey && group.ruleKey !== 'size')
            .map((group) => ({
                key: group.ruleKey!,
                label: GROUP_RULE_KEY_LABELS[group.ruleKey!] || group.name,
            })),
        [productOptionGroups],
    );

    const toggleCat = (id: string) => {
        setExpandedCats((prev) => (prev.includes(id) ? prev.filter((catId) => catId !== id) : [...prev, id]));
    };

    useEffect(() => {
        if (!showPreview || !arPublicUrl) {
            setArQrCodeDataUrl('');
            return;
        }

        QRCode.toDataURL(arPublicUrl, {
            margin: 1,
            width: 512,
            color: {
                dark: '#111111',
                light: '#F6F3EE',
            },
        })
            .then(setArQrCodeDataUrl)
            .catch((error) => {
                console.error('Failed to generate admin AR QR code', error);
                setArQrCodeDataUrl('');
            });
    }, [arPublicUrl, showPreview]);

    const downloadArQr = () => {
        if (!arQrCodeDataUrl || !establishmentSlug) return;
        const link = document.createElement('a');
        link.href = arQrCodeDataUrl;
        link.download = `webar-qr-${establishmentSlug}.png`;
        link.click();
    };

    const printArQr = () => {
        if (!arQrCodeDataUrl || !arPublicUrl) return;

        const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=640,height=860');
        if (!printWindow) return;

        printWindow.document.write(`
            <html>
                <head>
                    <title>QR WebAR</title>
                    <style>
                        body { font-family: Arial, sans-serif; background: #ffffff; color: #111111; margin: 0; padding: 32px; text-align: center; }
                        .card { max-width: 420px; margin: 0 auto; border: 1px solid #e5e5e5; border-radius: 24px; padding: 24px; }
                        img { width: 100%; max-width: 320px; border-radius: 16px; }
                        h1 { font-size: 24px; margin-bottom: 8px; }
                        p { font-size: 14px; line-height: 1.5; }
                        .url { margin-top: 16px; word-break: break-all; color: #555; }
                    </style>
                </head>
                <body>
                    <div class="card">
                        <h1>QR WebAR</h1>
                        <p>Escaneie para abrir a experiencia AR do restaurante.</p>
                        <img src="${arQrCodeDataUrl}" alt="QR WebAR" />
                        <p class="url">${arPublicUrl}</p>
                    </div>
                    <script>
                        window.onload = function() { window.print(); };
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleEditProduct = (product: any) => {
        if (!canManageMenu) return;
        setSelectedProduct(product);
        setProductOptionGroups((product.optionGroups || []).map((group: any) => ({
            id: group.id,
            name: group.name,
            ruleKey: group.ruleKey || undefined,
            required: !!group.required,
            maxSelect: Number(group.maxSelect) || 1,
            pricingMode: group.pricingMode === 'ABSOLUTE' ? 'ABSOLUTE' : 'EXTRA',
            items: (group.items || []).map((item: any) => ({
                id: item.id,
                name: item.name,
                price: Number(item.price) || 0,
                available: item.available !== false,
                selectionRules: Array.isArray(item.selectionRules) ? item.selectionRules : [],
            })),
        })));
        setSelected3dModelFile(null);
        setSelected3dUsdzFile(null);
        setRemove3dModelOnSave(false);
        setModel3dNotice('');
        setIsProductModalOpen(true);
    };

    const handleAddProduct = (categoryId: string) => {
        if (!canManageMenu) return;
        setSelectedProduct(categoryId ? { categoryId, price: 0 } : { price: 0 });
        setProductOptionGroups([]);
        setSelected3dModelFile(null);
        setSelected3dUsdzFile(null);
        setRemove3dModelOnSave(false);
        setModel3dNotice('');
        setIsProductModalOpen(true);
    };

    const addOptionGroup = () => {
        setProductOptionGroups((current) => [
            ...current,
            {
                id: `new-group-${Date.now()}-${current.length}`,
                name: '',
                ruleKey: undefined,
                required: false,
                maxSelect: 1,
                pricingMode: 'EXTRA',
                items: [],
            },
        ]);
    };

    const updateOptionGroup = (groupId: string, changes: Partial<ProductOptionGroupDraft>) => {
        setProductOptionGroups((current) =>
            current.map((group) => (group.id === groupId ? { ...group, ...changes } : group)),
        );
    };

    const removeOptionGroup = (groupId: string) => {
        setProductOptionGroups((current) => current.filter((group) => group.id !== groupId));
    };

    const addOptionItem = (groupId: string) => {
        setProductOptionGroups((current) =>
            current.map((group) =>
                group.id === groupId
                    ? {
                        ...group,
                        items: [
                            ...group.items,
                            {
                                id: `new-item-${Date.now()}-${group.items.length}`,
                                name: '',
                                price: 0,
                                available: true,
                            },
                        ],
                    }
                    : group,
            ),
        );
    };

    const updateOptionItem = (groupId: string, itemId: string, changes: Partial<ProductOptionGroupDraft['items'][number]>) => {
        setProductOptionGroups((current) =>
            current.map((group) =>
                group.id === groupId
                    ? {
                        ...group,
                        items: group.items.map((item) => (item.id === itemId ? { ...item, ...changes } : item)),
                    }
                    : group,
            ),
        );
    };

    const removeOptionItem = (groupId: string, itemId: string) => {
        setProductOptionGroups((current) =>
            current.map((group) =>
                group.id === groupId
                    ? { ...group, items: group.items.filter((item) => item.id !== itemId) }
                    : group,
            ),
        );
    };

    const applyPizzaPreset = () => {
        setProductOptionGroups(buildPizzaOptionPreset());
    };

    const applyAcaiPreset = (preset: AcaiPresetType = 'tradicional') => {
        setProductOptionGroups(buildAcaiOptionPreset(preset));
    };

    const updateOptionItemRule = (
        groupId: string,
        itemId: string,
        targetGroupKey: string,
        changes: Partial<ProductOptionSelectionRule>,
    ) => {
        setProductOptionGroups((current) =>
            current.map((group) =>
                group.id === groupId
                    ? {
                        ...group,
                        items: group.items.map((item) => {
                            if (item.id !== itemId) return item;

                            const currentRules = item.selectionRules || [];
                            const existingRule = currentRules.find((rule) => rule.targetGroupKey === targetGroupKey);
                            const nextRule: ProductOptionSelectionRule = {
                                targetGroupKey,
                                includedQuantity: existingRule?.includedQuantity ?? 0,
                                maxSelectOverride: existingRule?.maxSelectOverride ?? 1,
                                visible: existingRule?.visible ?? true,
                                ...changes,
                            };

                            const otherRules = currentRules.filter((rule) => rule.targetGroupKey !== targetGroupKey);

                            if (nextRule.visible === false && !nextRule.includedQuantity && !nextRule.maxSelectOverride) {
                                return { ...item, selectionRules: otherRules };
                            }

                            return {
                                ...item,
                                selectionRules: [...otherRules, nextRule],
                            };
                        }),
                    }
                    : group,
            ),
        );
    };

    const uploadProductImage = async (file: File | null) => {
        if (!file) return undefined;

        const formData = new FormData();
        formData.append('file', file);

        const { data } = await api.post('/products/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        return data.imageUrl as string;
    };

    const uploadProduct3dModel = async (productId: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        const { data } = await api.post(`/products/${productId}/3d-model`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        return data;
    };

    const uploadProduct3dUsdzAsset = async (productId: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        const { data } = await api.post(`/products/${productId}/3d-model-ios`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        return data;
    };

    const removeProduct3dModel = async (productId: string) => {
        const { data } = await api.delete(`/products/${productId}/3d-model`);
        return data;
    };

    const saveProduct = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!canManageMenu) return;

        try {
            setProductSaving(true);
            const formData = new FormData(e.currentTarget);
            const imageFile = formData.get('imageFile');
            const uploadedImageUrl = imageFile instanceof File && imageFile.size > 0
                ? await uploadProductImage(imageFile)
                : undefined;

            const data = {
                name: formData.get('name'),
                price: shouldHideBasePrice ? 0 : Number(selectedProduct?.price) || 0,
                categoryId: formData.get('categoryId'),
                description: formData.get('description'),
                imageUrl: uploadedImageUrl ?? selectedProduct?.imageUrl,
                available: formData.get('available') === 'on',
                establishmentId: user?.establishmentId,
                optionGroups: productOptionGroups
                    .map((group) => ({
                        name: group.name.trim(),
                        ruleKey: getNormalizedRuleKey(group.ruleKey),
                        required: group.required,
                        maxSelect: Math.max(1, Number(group.maxSelect) || 1),
                        pricingMode: group.pricingMode === 'ABSOLUTE' ? 'ABSOLUTE' : 'EXTRA',
                        items: group.items
                            .map((item) => ({
                                name: item.name.trim(),
                                price: Number(item.price) || 0,
                                available: item.available !== false,
                                selectionRules: (item.selectionRules || [])
                                    .map((rule) => ({
                                        targetGroupKey: rule.targetGroupKey,
                                        includedQuantity: Math.max(0, Number(rule.includedQuantity) || 0),
                                        maxSelectOverride: rule.maxSelectOverride ? Math.max(1, Number(rule.maxSelectOverride) || 1) : undefined,
                                        visible: rule.visible !== false,
                                    }))
                                    .filter((rule) => rule.targetGroupKey),
                            }))
                            .filter((item) => item.name),
                    }))
                    .filter((group) => group.name && group.items.length > 0),
            };

            const productResponse = selectedProduct?.id
                ? await api.patch(`/products/${selectedProduct.id}`, data)
                : await api.post('/products', data);
            const savedProduct = productResponse.data;

            if (threeDModelFeatureEnabled) {
                if (selected3dModelFile) {
                    await uploadProduct3dModel(savedProduct.id, selected3dModelFile);
                }

                if (selected3dUsdzFile) {
                    await uploadProduct3dUsdzAsset(savedProduct.id, selected3dUsdzFile);
                }
            }

            if (remove3dModelOnSave && selectedProduct?.id && !selected3dModelFile && !selected3dUsdzFile) {
                await removeProduct3dModel(selectedProduct.id);
            }

            setIsProductModalOpen(false);
            setProductOptionGroups([]);
            setSelected3dModelFile(null);
            setSelected3dUsdzFile(null);
            setRemove3dModelOnSave(false);
            setModel3dNotice('');
            await loadData();
        } catch (error) {
            console.error(error);
            alert('Erro ao salvar produto');
        } finally {
            setProductSaving(false);
        }
    };

    const saveCategory = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!canManageMenu) return;

        try {
            setCategorySaving(true);
            const formData = new FormData(e.currentTarget);
            const data = {
                name: formData.get('name'),
                establishmentId: user?.establishmentId,
            };

            if (selectedCategory?.id) {
                await api.patch(`/categories/${selectedCategory.id}`, data);
            } else {
                await api.post('/categories', data);
            }

            setIsCategoryModalOpen(false);
            await loadData();
        } catch (error) {
            console.error(error);
            alert('Erro ao salvar categoria');
        } finally {
            setCategorySaving(false);
        }
    };

    const deleteProduct = async (productId: string) => {
        if (!canManageMenu) return;
        if (!window.confirm('Excluir este produto?')) return;
        try {
            setDeletingProductId(productId);
            await api.delete(`/products/${productId}`);
            await loadData();
        } catch (error: any) {
            console.error(error);
            alert(error?.response?.data?.message || 'Erro ao excluir produto');
        } finally {
            setDeletingProductId(null);
        }
    };

    const deleteCategory = async (category: any) => {
        if (!canManageMenu) return;
        if ((category.products?.length ?? 0) > 0) {
            alert('Remova os produtos da categoria antes de excluí-la.');
            return;
        }

        if (!window.confirm(`Excluir a categoria "${category.name}"?`)) return;

        try {
            setDeletingCategoryId(category.id);
            await api.delete(`/categories/${category.id}`);
            await loadData();
        } catch (error) {
            console.error(error);
            alert('Erro ao excluir categoria');
        } finally {
            setDeletingCategoryId(null);
        }
    };

    const toggleProductAvailability = async (product: any) => {
        if (!canManageMenu) return;
        try {
            setTogglingProductId(product.id);
            await api.patch(`/products/${product.id}`, { available: !product.available });
            await loadData();
        } catch (error) {
            console.error(error);
            alert('Erro ao atualizar disponibilidade');
        } finally {
            setTogglingProductId(null);
        }
    };

    const printMenu = () => window.print();

    const productSubmitLabel = selectedProduct?.id ? 'Salvar Alterações' : 'Criar Produto';
    const categorySubmitLabel = selectedCategory?.id ? 'Salvar Categoria' : 'Criar Categoria';

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white">Cardapio</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Gerencie categorias, produtos e opcionais.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {isOffline ? (
                        <div className="flex flex-col items-end">
                            <span className="badge flex items-center gap-1.5 border-amber-500/50 bg-amber-500/10 text-amber-500">
                                <WifiOff className="h-3.5 w-3.5" />
                                Modo Offline de Segurança
                            </span>
                            {lastSync && (
                                <span className="mt-1 text-[10px] text-amber-200/50">
                                    Última sincronização: {new Date(lastSync).toLocaleTimeString('pt-BR')}
                                </span>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-end">
                            <span className="badge flex items-center gap-1.5 border-emerald-500/50 bg-emerald-500/10 text-emerald-400">
                                <ShieldCheck className="h-3.5 w-3.5" />
                                Cardápio Sincronizado
                            </span>
                            {lastSync && (
                                <span className="mt-1 text-[10px] text-emerald-400/40">
                                    Dados protegidos localmente
                                </span>
                            )}
                        </div>
                    )}
                    {canManageMenu && (
                        <>
                            <button
                                onClick={() => { setSelectedCategory(null); setIsCategoryModalOpen(true); }}
                                className="btn-secondary flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                Nova Categoria
                            </button>
                            <button
                                onClick={() => handleAddProduct('')}
                                className="btn-primary flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                Novo Produto
                            </button>
                        </>
                    )}
                    {!canManageMenu && (
                        <div className="badge badge-muted flex items-center gap-2">
                            <Lock className="w-3.5 h-3.5" />
                            Edição restrita ao admin
                        </div>
                    )}
                </div>
            </div>

            <div className="card p-4 flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Buscar produtos ou categorias..."
                        className="input pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="flex gap-2">
                    <button onClick={() => { setSelectedPreviewArProduct(null); setShowPreview(true); }} className="btn-secondary text-sm flex items-center gap-2">
                        <Smartphone className="w-4 h-4" />
                        Versão Mobile
                    </button>
                    <button onClick={printMenu} className="btn-secondary text-sm flex items-center gap-2">
                        <Printer className="w-4 h-4" />
                        Impressão
                    </button>
                </div>
            </div>

            {isLoading ? (
                <div className="text-center p-8 text-muted-foreground">Carregando cardápio...</div>
            ) : loadError ? (
                <div className="card p-8 space-y-3">
                    <h2 className="text-lg font-semibold text-white">Cardapio indisponivel</h2>
                    <p className="text-sm text-muted-foreground">{loadError}</p>
                    <p className="text-xs text-muted-foreground">
                        Se voce publicou mudancas recentes no schema do backend, rode <code>npm run db:push</code> na API.
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredCategories.map((category) => (
                        <div key={category.id} className="card overflow-hidden group">
                            <div
                                className="bg-[#1A1A1A]/30 px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-[#1A1A1A]/50 transition-all"
                                onClick={() => toggleCat(category.id)}
                            >
                                <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity" />
                                <div className="flex-1 flex items-center gap-3">
                                    <h3 className="font-bold text-white">{category.name}</h3>
                                    <span className="badge-muted text-[10px] uppercase font-bold tracking-wider">{category.products?.length || 0} ITENS</span>
                                </div>
                                {canManageMenu && (
                                    <div className="flex items-center gap-2" onClick={(evt) => evt.stopPropagation()}>
                                        <button onClick={() => { setSelectedCategory(category); setIsCategoryModalOpen(true); }} className="p-2 rounded-lg hover:bg-surface-2 text-muted-foreground hover:text-white transition-all">
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => deleteCategory(category)} disabled={deletingCategoryId === category.id} className="p-2 rounded-lg hover:bg-surface-2 text-muted-foreground hover:text-white transition-all disabled:cursor-not-allowed disabled:opacity-60">
                                            {deletingCategoryId === category.id ? <span className="text-[11px] font-semibold">...</span> : <Trash2 className="w-4 h-4" />}
                                        </button>
                                        <div className="w-px h-4 bg-border mx-1" />
                                        {expandedCats.includes(category.id) ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                                    </div>
                                )}
                            </div>

                            {expandedCats.includes(category.id) && (
                                <div className="border-t border-[#2A2A2A]">
                                    <div className="divide-y divide-[#2A2A2A] md:hidden">
                                        {(category.products || []).map((product: any) => (
                                            <div key={product.id} className="space-y-3 px-4 py-4">
                                                <div className="flex items-start gap-3">
                                                    <div className="h-14 w-14 shrink-0 rounded-lg border border-[#2A2A2A] bg-[#1A1A1A] overflow-hidden flex items-center justify-center">
                                                        {product.imageUrl ? <img src={resolveAssetUrl(product.imageUrl)} alt="" className="h-full w-full object-cover" /> : <span className="text-[10px] text-muted-foreground">IMG</span>}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-semibold text-white">{product.name}</p>
                                                                <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{product.description || 'Sem descricao'}</p>
                                                            </div>
                                                            <p className="shrink-0 text-sm font-bold text-white">{formatPrice(getProductDisplayPrice(product))}</p>
                                                        </div>
                                                        <div className="mt-2 flex flex-wrap items-center gap-2">
                                                            {product.model3dEnabled && product.model3dGlbUrl && (
                                                                <span className="inline-flex rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                                                                    3D / AR
                                                                </span>
                                                            )}
                                                            <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${product.available ? 'border border-emerald-500/20 bg-emerald-500/10 text-emerald-300' : 'border border-white/10 bg-white/5 text-muted-foreground'}`}>
                                                                {product.available ? 'Disponivel' : 'Pausado'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                            onClick={() => toggleProductAvailability(product)}
                                                            disabled={!canManageMenu || togglingProductId === product.id}
                                                            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition-all ${product.available ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300' : 'border-white/10 bg-white/5 text-muted-foreground'} ${!canManageMenu ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                        >
                                                            {product.available ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                                        {togglingProductId === product.id ? 'Salvando...' : product.available ? 'Ativo' : 'Oculto'}
                                                    </button>
                                                    {canManageMenu ? (
                                                        <>
                                                            <button
                                                                onClick={() => handleEditProduct(product)}
                                                                className="inline-flex items-center gap-2 rounded-xl border border-primary/20 bg-primary/10 px-3 py-2 text-xs font-semibold text-primary transition-all hover:bg-primary/15"
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                                Editar
                                                            </button>
                                                            <button
                                                                onClick={() => deleteProduct(product.id)}
                                                                disabled={deletingProductId === product.id}
                                                                className="inline-flex items-center gap-2 rounded-xl border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs font-semibold text-destructive transition-all hover:bg-destructive/15 disabled:cursor-not-allowed disabled:opacity-60"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                                {deletingProductId === product.id ? 'Excluindo...' : 'Excluir'}
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <span className="text-[11px] text-muted-foreground">Somente leitura</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        {canManageMenu && (
                                            <div className="px-4 py-4">
                                                <button
                                                    onClick={() => handleAddProduct(category.id)}
                                                    className="flex items-center gap-2 text-sm text-primary font-medium hover:text-primary-hover transition-colors"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                    Adicionar item em {category.name}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <table className="hidden w-full md:table">
                                        <tbody>
                                            {(category.products || []).map((product: any) => (
                                                <tr key={product.id} className="border-b border-[#2A2A2A] last:border-0 hover:bg-[#1A1A1A]/20 transition-colors">
                                                    <td className="py-3 px-6 w-12">
                                                        <div className="w-12 h-12 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-center overflow-hidden">
                                                            {product.imageUrl ? <img src={resolveAssetUrl(product.imageUrl)} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-muted-foreground">IMG</span>}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4 flex-1">
                                                        <p className="font-semibold text-white text-sm">{product.name}</p>
                                                        <p className="text-xs text-muted-foreground truncate max-w-md">{product.description || 'Sem descrição'}</p>
                                                        {product.model3dEnabled && product.model3dGlbUrl && (
                                                            <span className="mt-1 inline-flex rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                                                                3D / AR
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-4 text-right">
                                                        <p className="font-bold text-white text-sm">{formatPrice(getProductDisplayPrice(product))}</p>
                                                    </td>
                                                    <td className="py-3 px-4 text-center">
                                                        <button
                                                            onClick={() => toggleProductAvailability(product)}
                                                            disabled={!canManageMenu || togglingProductId === product.id}
                                                            className={`p-1.5 rounded-lg transition-all ${product.available ? 'text-success hover:bg-success/10' : 'text-muted-foreground hover:bg-white/5'} ${!canManageMenu ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                        >
                                                            {togglingProductId === product.id ? <span className="text-[11px] font-semibold">...</span> : product.available ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                                        </button>
                                                    </td>
                                                    <td className="py-3 px-4 text-right pr-6">
                                                        {canManageMenu ? (
                                                            <div className="flex justify-end gap-2">
                                                                <button
                                                                    onClick={() => handleEditProduct(product)}
                                                                    className="p-2 rounded-lg hover:bg-surface-2 text-muted-foreground hover:text-white transition-all"
                                                                >
                                                                    <MoreVertical className="w-4 h-4" />
                                                                </button>
                                                                <button onClick={() => deleteProduct(product.id)} disabled={deletingProductId === product.id} className="p-2 rounded-lg hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-all disabled:cursor-not-allowed disabled:opacity-60">
                                                                    {deletingProductId === product.id ? <span className="text-[11px] font-semibold">...</span> : <Trash2 className="w-4 h-4" />}
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex justify-end">
                                                                <span className="text-[11px] text-muted-foreground">Somente leitura</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {canManageMenu && (
                                                <tr className="hover:bg-[#1A1A1A]/20 transition-colors">
                                                    <td colSpan={5} className="py-4 px-6">
                                                        <button
                                                            onClick={() => handleAddProduct(category.id)}
                                                            className="flex items-center gap-2 text-sm text-primary font-medium hover:text-primary-hover transition-colors"
                                                        >
                                                            <Plus className="w-4 h-4" />
                                                            Adicionar item em {category.name}
                                                        </button>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    ))}
                    {filteredCategories.length === 0 && (
                        <div className="card p-8 text-center text-muted-foreground">
                            Nenhum item encontrado para a busca atual.
                        </div>
                    )}
                </div>
            )}

            <Modal
                isOpen={isProductModalOpen}
                onClose={() => {
                    if (productSaving) return;
                    setIsProductModalOpen(false);
                    setProductOptionGroups([]);
                    setSelected3dModelFile(null);
                    setSelected3dUsdzFile(null);
                    setRemove3dModelOnSave(false);
                    setModel3dNotice('');
                }}
                size="2xl"
                title={selectedProduct?.id ? 'Editar Produto' : 'Novo Produto'}
                description="Os campos com * são obrigatórios"
                footer={(
                    <>
                        <button
                            type="button"
                            onClick={() => {
                                if (productSaving) return;
                                setIsProductModalOpen(false);
                                setProductOptionGroups([]);
                                setSelected3dModelFile(null);
                                setSelected3dUsdzFile(null);
                                setRemove3dModelOnSave(false);
                                setModel3dNotice('');
                            }}
                            disabled={productSaving}
                            className="btn-secondary w-full sm:w-auto disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Cancelar
                        </button>
                        <button type="submit" form="productForm" disabled={productSaving} className="btn-primary w-full sm:w-auto disabled:cursor-not-allowed disabled:opacity-60">{productSaving ? (selectedProduct?.id ? 'Salvando...' : 'Criando...') : productSubmitLabel}</button>
                    </>
                )}
            >
                <form id="productForm" onSubmit={saveProduct} className="space-y-6">
                    <div className="space-y-4 rounded-2xl border border-[#2A2A2A] bg-[#111] p-4 sm:p-5">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-white">Nome do Produto *</label>
                        <input name="name" required type="text" className="input" placeholder="Ex: Filé ao Molho Madeira" defaultValue={selectedProduct?.name} />
                    </div>
                    <div className={`grid gap-4 ${shouldHideBasePrice ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
                        {!shouldHideBasePrice && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-white">Preço (R$) *</label>
                                <div className="relative">
                                    <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">R$</span>
                                    <input
                                        name="price"
                                        required
                                        type="text"
                                        inputMode="numeric"
                                        className="input pl-12"
                                        placeholder="0,00"
                                        value={formatPriceInputValue(Number(selectedProduct?.price) || 0)}
                                        onChange={(event) => setSelectedProduct((current: any) => ({
                                            ...(current || {}),
                                            price: parsePriceInputValue(event.target.value),
                                        }))}
                                    />
                                </div>
                            </div>
                        )}
                        {shouldHideBasePrice && (
                            <div className="space-y-2 rounded-xl border border-primary/20 bg-primary/10 px-4 py-3">
                                <p className="text-sm font-medium text-white">Preço definido nas opções</p>
                                <p className="text-xs text-white/70">
                                    Esse produto usa um grupo com <span className="font-semibold text-white">preço final da opção</span>. O valor principal fica oculto e o total será definido pelo cliente ao escolher tamanho ou variação.
                                </p>
                                <input name="price" type="hidden" value="0" />
                            </div>
                        )}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-white">Categoria *</label>
                            <select name="categoryId" required className="input appearance-none bg-[#1A1A1A]" defaultValue={selectedProduct?.categoryId ?? ''}>
                                <option value="">Selecione...</option>
                                {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-white">Descrição</label>
                        <textarea name="description" className="input min-h-[100px] resize-none" placeholder="Descreva os ingredientes e detalhes do prato..." defaultValue={selectedProduct?.description}></textarea>
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-white">Imagem do produto</label>
                        <input
                            name="imageFile"
                            type="file"
                            accept="image/*"
                            className="block w-full rounded-xl border border-[#2A2A2A] bg-[#151515] px-3 py-3 text-sm text-muted-foreground file:mb-3 file:mr-0 file:w-full file:rounded-lg file:border-0 file:bg-primary file:px-4 file:py-2.5 file:font-medium file:text-white sm:file:mb-0 sm:file:mr-3 sm:file:w-auto"
                        />
                        {selectedProduct?.imageUrl && (
                            <p className="text-xs text-muted-foreground">Se nenhum arquivo novo for enviado, a imagem atual será mantida.</p>
                        )}
                    </div>
                    <div className="space-y-4 rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                        <div>
                            <p className="text-sm font-semibold text-white">Modelo 3D / AR</p>
                            <p className="mt-1 text-xs text-muted-foreground">
                                O modelo 3D é criado fora do Garçom Digital, no celular. Aqui o sistema só valida e armazena o `.glb`.
                            </p>
                        </div>

                        <div className="rounded-xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-white/80">
                            <p className="font-semibold text-white">Fluxo recomendado</p>
                            <ol className="mt-2 space-y-1 text-xs leading-5 text-white/75">
                                <li>1. Use Scaniverse ou Polycam no celular.</li>
                                <li>2. Escaneie o prato e exporte o arquivo em `.glb`.</li>
                                <li>3. Volte ao Garçom Digital e envie esse `.glb` aqui.</li>
                                <li>4. A página pública passa a mostrar a visualização 3D e já fica preparada para AR.</li>
                            </ol>
                        </div>

                        {!threeDModelFeatureEnabled ? (
                            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-4 text-sm text-amber-100">
                                <p className="font-semibold">Recurso premium</p>
                                <p className="mt-1">{threeDModelAccessHint}</p>
                                <a href="/admin/configuracoes" className="mt-3 inline-flex text-sm font-semibold text-white">
                                    Ver assinatura e upgrade
                                </a>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="rounded-xl border border-[#2A2A2A] bg-[#101010] px-4 py-3 text-sm text-muted-foreground">
                                    Aceita apenas `.glb` com até 15 MB. Sem fotogrametria no servidor, sem filas pesadas, sem processamento no Railway.
                                </div>
                                <input
                                    name="model3dFile"
                                    type="file"
                                    accept=".glb,model/gltf-binary"
                                    onChange={(event) => {
                                        const file = event.target.files?.[0] || null;
                                        setSelected3dModelFile(file);
                                        setRemove3dModelOnSave(false);
                                        setModel3dNotice(file ? `Arquivo GLB selecionado: ${file.name}` : '');
                                    }}
                                    className="block w-full rounded-xl border border-[#2A2A2A] bg-[#151515] px-3 py-3 text-sm text-muted-foreground file:mb-3 file:mr-0 file:w-full file:rounded-lg file:border-0 file:bg-primary file:px-4 file:py-2.5 file:font-medium file:text-white sm:file:mb-0 sm:file:mr-3 sm:file:w-auto"
                                />
                                <input
                                    name="model3dUsdzFile"
                                    type="file"
                                    accept=".usdz,model/vnd.usdz+zip,model/usd"
                                    onChange={(event) => {
                                        const file = event.target.files?.[0] || null;
                                        setSelected3dUsdzFile(file);
                                        setRemove3dModelOnSave(false);
                                        setModel3dNotice(file ? `Arquivo USDZ selecionado: ${file.name}` : '');
                                    }}
                                    className="block w-full rounded-xl border border-[#2A2A2A] bg-[#151515] px-3 py-3 text-sm text-muted-foreground file:mb-3 file:mr-0 file:w-full file:rounded-lg file:border-0 file:bg-primary file:px-4 file:py-2.5 file:font-medium file:text-white sm:file:mb-0 sm:file:mr-3 sm:file:w-auto"
                                />
                                {(selectedProduct?.model3dGlbUrl || selected3dModelFile) && (
                                    <div className="rounded-xl border border-[#2A2A2A] bg-[#101010] px-4 py-3 text-sm text-white">
                                        <p className="font-semibold">
                                            {selected3dModelFile ? 'Novo arquivo pronto para envio' : 'Modelo 3D atual'}
                                        </p>
                                        <p className="mt-1 text-xs text-muted-foreground">
                                            {selected3dModelFile
                                                ? `${selected3dModelFile.name} • ${formatFileSize(selected3dModelFile.size)}`
                                                : `${selectedProduct?.model3dGlbOriginalFileName || 'Arquivo .glb ativo'}${selectedProduct?.model3dGlbFileSize ? ` • ${formatFileSize(selectedProduct.model3dGlbFileSize)}` : ''}`}
                                        </p>
                                        {!selected3dModelFile && selectedProduct?.model3dGlbUrl && (
                                            <div className="mt-3 flex flex-wrap gap-3">
                                                <a
                                                    href={resolveAssetUrl(selectedProduct.model3dGlbUrl)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-xs font-semibold text-primary"
                                                >
                                                    Abrir arquivo atual
                                                </a>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setRemove3dModelOnSave((current) => !current);
                                                        setModel3dNotice(!remove3dModelOnSave ? 'O modelo atual será removido ao salvar.' : '');
                                                    }}
                                                    className="text-xs font-semibold text-red-300"
                                                >
                                                    {remove3dModelOnSave ? 'Desfazer remoção' : 'Remover ao salvar'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {(selectedProduct?.model3dUsdzUrl || selected3dUsdzFile) && (
                                    <div className="rounded-xl border border-[#2A2A2A] bg-[#101010] px-4 py-3 text-sm text-white">
                                        <p className="font-semibold">
                                            {selected3dUsdzFile ? 'Arquivo USDZ pronto para envio' : 'Arquivo USDZ atual'}
                                        </p>
                                        <p className="mt-1 text-xs text-muted-foreground">
                                            {selected3dUsdzFile
                                                ? `${selected3dUsdzFile.name} • ${formatFileSize(selected3dUsdzFile.size)}`
                                                : `${selectedProduct?.model3dUsdzOriginalFileName || 'Arquivo .usdz ativo'}${selectedProduct?.model3dUsdzFileSize ? ` • ${formatFileSize(selectedProduct.model3dUsdzFileSize)}` : ''}`}
                                        </p>
                                        {!selected3dUsdzFile && selectedProduct?.model3dUsdzUrl && (
                                            <div className="mt-3 flex flex-wrap gap-3">
                                                <a
                                                    href={resolveAssetUrl(selectedProduct.model3dUsdzUrl)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-xs font-semibold text-primary"
                                                >
                                                    Abrir USDZ atual
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {!selectedProduct?.id && (
                                    <p className="text-xs text-muted-foreground">
                                        Em produtos novos, o `.glb` selecionado será enviado automaticamente logo após o primeiro salvamento.
                                    </p>
                                )}
                                {model3dNotice && (
                                    <div className="rounded-xl border border-primary/20 bg-primary/10 px-3 py-2 text-xs text-white/80">
                                        {model3dNotice}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    </div>
                    <div className="space-y-4 rounded-2xl border border-[#2A2A2A] bg-[#111] p-4 sm:p-5">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                <p className="text-sm font-semibold text-white">Opcoes do produto</p>
                                <p className="text-xs text-muted-foreground">
                                    Crie grupos como Tamanho, Borda recheada, Sabores ou Extras. O restaurante decide o que o cliente pode escolher.
                                </p>
                            </div>
                            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                                {isPizzaProductFlow && (
                                    <button type="button" onClick={applyPizzaPreset} className="btn-primary text-sm w-full sm:w-auto">
                                        Usar modelo de pizza
                                    </button>
                                )}
                                {isAcaiProductFlow && (
                                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                                        <button type="button" onClick={() => applyAcaiPreset('tradicional')} className="btn-primary text-sm w-full sm:w-auto">
                                            Açaí Tradicional
                                        </button>
                                        <button type="button" onClick={() => applyAcaiPreset('tropical')} className="btn-secondary text-sm w-full sm:w-auto">
                                            Açaí Tropical
                                        </button>
                                        <button type="button" onClick={() => applyAcaiPreset('puro')} className="btn-secondary text-sm w-full sm:w-auto">
                                            Açaí Puro
                                        </button>
                                    </div>
                                )}
                                <button type="button" onClick={addOptionGroup} className="btn-secondary text-sm w-full sm:w-auto">
                                    Adicionar grupo
                                </button>
                            </div>
                        </div>
                        {isPizzaProductFlow && (
                            <div className="rounded-xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-white/80">
                                Para pizza, o fluxo recomendado e: categoria <span className="font-semibold text-white">Pizzas</span> {'->'} produto <span className="font-semibold text-white">Pizza de Calabresa</span> {'->'} grupos <span className="font-semibold text-white">Tamanho</span>, <span className="font-semibold text-white">Borda recheada</span> e <span className="font-semibold text-white">Extras</span>.
                            </div>
                        )}
                        {isAcaiProductFlow && (
                            <div className="rounded-xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-white/80">
                                Escolha primeiro um modelo de açaí. O grupo <span className="font-semibold text-white">Tamanho / volume</span> controla o preço final e quantos itens grátis o cliente recebe em <span className="font-semibold text-white">Acréscimos simples</span>, <span className="font-semibold text-white">Frutas</span> ou <span className="font-semibold text-white">Cremes</span>.
                            </div>
                        )}
                        {productOptionGroups.length === 0 && (
                            <div className="rounded-xl border border-dashed border-[#2A2A2A] px-4 py-5 text-sm text-muted-foreground">
                                Exemplo: Pizza de Calabresa com grupos Tamanho, Borda recheada e Extras, ou Açaí Tradicional com tamanho, simples, frutas e cremes.
                            </div>
                        )}
                        {productOptionGroups.map((group, groupIndex) => (
                            <div key={group.id} className="space-y-4 rounded-2xl border border-[#2A2A2A] bg-[#0F0F0F] p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-sm font-semibold text-white">Grupo {groupIndex + 1}</p>
                                        <p className="text-xs text-muted-foreground">Defina nome, limite de escolhas e a regra de preço deste grupo.</p>
                                    </div>
                                    <button type="button" onClick={() => removeOptionGroup(group.id)} className="p-2 rounded-lg hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-all">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-white">Nome do grupo</label>
                                        <input type="text" className="input" value={group.name} onChange={(event) => updateOptionGroup(group.id, { name: event.target.value })} placeholder="Ex: Tamanho" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-white">Maximo de escolhas</label>
                                        <input type="number" min="1" className="input" value={group.maxSelect} onChange={(event) => updateOptionGroup(group.id, { maxSelect: Math.max(1, Number(event.target.value) || 1) })} />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-white">Funcao deste grupo no produto</label>
                                    <select
                                        className="input appearance-none bg-[#1A1A1A]"
                                        value={(group.ruleKey as ProductGroupRuleKey) || 'custom'}
                                        onChange={(event) => updateOptionGroup(group.id, {
                                            ruleKey: event.target.value === 'custom' ? undefined : event.target.value,
                                        })}
                                    >
                                        {GROUP_RULE_TYPE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-muted-foreground">
                                        {GROUP_RULE_TYPE_OPTIONS.find((option) => option.value === ((group.ruleKey as ProductGroupRuleKey) || 'custom'))?.description}
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-white">Como esse grupo altera o preco</label>
                                    <select
                                        className="input appearance-none bg-[#1A1A1A]"
                                        value={group.pricingMode}
                                        onChange={(event) => updateOptionGroup(group.id, { pricingMode: event.target.value as 'EXTRA' | 'ABSOLUTE' })}
                                    >
                                        <option value="EXTRA">Preco extra sobre o produto</option>
                                        <option value="ABSOLUTE">Preco final da opcao</option>
                                    </select>
                                    <p className="text-xs text-muted-foreground">
                                        {group.pricingMode === 'ABSOLUTE'
                                            ? 'Use para tamanho de pizza ou qualquer escolha que defina o preco final.'
                                            : 'Use para borda recheada, extras e opcionais que somam ao valor do produto.'}
                                    </p>
                                </div>
                                <label className="flex items-center gap-2 text-sm text-white">
                                    <input type="checkbox" className="w-4 h-4 accent-primary" checked={group.required} onChange={(event) => updateOptionGroup(group.id, { required: event.target.checked })} />
                                    O cliente precisa escolher este grupo
                                </label>
                                <div className="space-y-3">
                                    {group.items.map((item, itemIndex) => (
                                        <div key={item.id} className="space-y-3 rounded-xl border border-[#2A2A2A] bg-[#151515] p-3">
                                            <div className="space-y-2">
                                                <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Opcao {itemIndex + 1}</label>
                                                <input type="text" className="input" value={item.name} onChange={(event) => updateOptionItem(group.id, item.id, { name: event.target.value })} placeholder="Ex: Grande" />
                                            </div>
                                            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                                                <div className="space-y-2">
                                                    <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                                        {group.pricingMode === 'ABSOLUTE' ? 'Preco final' : 'Preco extra'}
                                                    </label>
                                                    <div className="relative">
                                                        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">R$</span>
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            className="input pl-12"
                                                            placeholder="0,00"
                                                            value={formatPriceInputValue(item.price)}
                                                            onChange={(event) => updateOptionItem(group.id, item.id, { price: parsePriceInputValue(event.target.value) })}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between gap-3 sm:justify-end">
                                                    <label className="flex items-center gap-2 text-xs text-white">
                                                        <input type="checkbox" className="w-4 h-4 accent-primary" checked={item.available} onChange={(event) => updateOptionItem(group.id, item.id, { available: event.target.checked })} />
                                                        Disponivel
                                                    </label>
                                                    <button type="button" onClick={() => removeOptionItem(group.id, item.id)} className="p-2 rounded-lg hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-all">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                            {group.ruleKey === 'size' && targetableGroupOptions.length > 0 && (
                                                <div className="rounded-xl border border-primary/10 bg-primary/5 p-3 space-y-3">
                                                    <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                                                        Beneficios deste tamanho
                                                    </p>
                                                    <div className="space-y-3">
                                                        {targetableGroupOptions.map((targetGroup) => {
                                                            const currentRule = getSelectionRuleForGroup(item, targetGroup.key);

                                                            return (
                                                                <div key={targetGroup.key} className="grid gap-3 md:grid-cols-3">
                                                                    <div className="space-y-1 md:col-span-1">
                                                                        <p className="text-sm font-medium text-white">{targetGroup.label}</p>
                                                                        <p className="text-xs text-muted-foreground">
                                                                            Defina quantos itens ficam gratis e quantos o cliente pode marcar.
                                                                        </p>
                                                                    </div>
                                                                    <div className="space-y-2">
                                                                        <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                                                            Itens grátis neste grupo
                                                                        </label>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            className="input"
                                                                            value={currentRule?.includedQuantity ?? 0}
                                                                            onChange={(event) => updateOptionItemRule(group.id, item.id, targetGroup.key, {
                                                                                includedQuantity: Math.max(0, Number(event.target.value) || 0),
                                                                            })}
                                                                        />
                                                                    </div>
                                                                    <div className="space-y-2">
                                                                        <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                                                            Limite total de escolhas
                                                                        </label>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            className="input"
                                                                            value={currentRule?.maxSelectOverride ?? 0}
                                                                            onChange={(event) => {
                                                                                const nextValue = Math.max(0, Number(event.target.value) || 0);
                                                                                updateOptionItemRule(group.id, item.id, targetGroup.key, {
                                                                                    maxSelectOverride: nextValue === 0 ? undefined : nextValue,
                                                                                    visible: nextValue !== 0 || (currentRule?.includedQuantity ?? 0) > 0,
                                                                                });
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <div className="space-y-1 md:col-span-3">
                                                                        <p className="text-[11px] text-muted-foreground">
                                                                            {(currentRule?.maxSelectOverride ?? 0) === 0 && (currentRule?.includedQuantity ?? 0) === 0
                                                                                ? `Esse tamanho mantem ${targetGroup.label.toLowerCase()} escondido para o cliente.`
                                                                                : (currentRule?.includedQuantity ?? 0) > 0 && (currentRule?.maxSelectOverride ?? 0) > 0
                                                                                    ? `O cliente poderá escolher até ${currentRule?.maxSelectOverride} item(ns) em ${targetGroup.label.toLowerCase()}, com ${currentRule?.includedQuantity} gratuito(s).`
                                                                                    : (currentRule?.maxSelectOverride ?? 0) > 0
                                                                                        ? `O cliente poderá escolher até ${currentRule?.maxSelectOverride} item(ns) em ${targetGroup.label.toLowerCase()}.`
                                                                                        : `O cliente receberá ${currentRule?.includedQuantity ?? 0} item(ns) gratuito(s) em ${targetGroup.label.toLowerCase()}.`}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => addOptionItem(group.id)} className="btn-secondary text-sm">
                                        Adicionar opcao
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <input type="checkbox" name="available" id="available" className="w-4 h-4 accent-primary" defaultChecked={selectedProduct?.available !== false} />
                        <label htmlFor="available" className="text-sm text-white font-medium">Produto disponível para venda</label>
                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={isCategoryModalOpen}
                onClose={() => {
                    if (!categorySaving) {
                        setIsCategoryModalOpen(false);
                    }
                }}
                title={selectedCategory?.id ? 'Editar Categoria' : 'Nova Categoria'}
                size="sm"
                footer={(
                    <>
                        <button type="button" onClick={() => setIsCategoryModalOpen(false)} disabled={categorySaving} className="btn-secondary disabled:cursor-not-allowed disabled:opacity-60">Cancelar</button>
                        <button type="submit" form="categoryForm" disabled={categorySaving} className="btn-primary disabled:cursor-not-allowed disabled:opacity-60">{categorySaving ? (selectedCategory?.id ? 'Salvando...' : 'Criando...') : categorySubmitLabel}</button>
                    </>
                )}
            >
                <form id="categoryForm" onSubmit={saveCategory} className="space-y-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-white">Nome da Categoria</label>
                        <input name="name" required type="text" className="input" placeholder="Ex: Bebidas, Sobremesas..." defaultValue={selectedCategory?.name} />
                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={showPreview}
                onClose={() => setShowPreview(false)}
                title="Prévia Mobile do Cardápio"
                size="md"
                footer={<button type="button" onClick={() => setShowPreview(false)} className="btn-primary">Fechar</button>}
            >
                <div className="mx-auto w-full max-w-sm rounded-[2rem] border border-[#2A2A2A] bg-[#0B0B0B] p-4 space-y-4">
                    <div className="text-center">
                        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">QR Menu</p>
                        <h3 className="text-lg font-bold text-white mt-2">Prévia para clientes</h3>
                    </div>
                    <div className="rounded-2xl border border-primary/20 bg-primary/10 p-4 space-y-4">
                        <div className="text-center">
                            <p className="text-xs uppercase tracking-[0.3em] text-primary/75">QR WebAR para impressao</p>
                            <h4 className="mt-2 text-base font-bold text-white">
                                {activeArProduct ? `Destino atual: ${activeArProduct.name}` : 'Nenhum produto 3D ativo'}
                            </h4>
                            <p className="mt-2 text-xs text-white/70">
                                O dono do restaurante imprime este QR. O cliente final apenas escaneia e entra na experiencia AR.
                            </p>
                        </div>
                        {activeArProduct && arQrCodeDataUrl ? (
                            <div className="overflow-hidden rounded-[1.5rem] bg-[#F6F3EE] p-3">
                                <img src={arQrCodeDataUrl} alt="QR Code WebAR do restaurante" className="w-full rounded-2xl" />
                            </div>
                        ) : (
                            <div className="rounded-2xl border border-dashed border-white/15 bg-black/20 px-4 py-5 text-center text-sm text-white/60">
                                Ative um produto com modelo 3D `.glb` para gerar o QR WebAR.
                            </div>
                        )}
                        {arPublicUrl && (
                            <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-left">
                                <p className="text-[11px] uppercase tracking-[0.24em] text-white/45">Link publico</p>
                                <p className="mt-2 break-all text-xs text-white/75">{arPublicUrl}</p>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                            <a
                                href={arPublicUrl || '#'}
                                target="_blank"
                                rel="noreferrer"
                                className={`btn-secondary text-sm text-center ${!arPublicUrl ? 'pointer-events-none opacity-50' : ''}`}
                            >
                                Abrir AR
                            </a>
                            <a
                                href={qrManagementUrl || '#'}
                                target="_blank"
                                rel="noreferrer"
                                className={`btn-secondary text-sm text-center ${!qrManagementUrl ? 'pointer-events-none opacity-50' : ''}`}
                            >
                                Abrir pagina do QR
                            </a>
                            <button
                                type="button"
                                onClick={downloadArQr}
                                disabled={!arQrCodeDataUrl}
                                className="btn-secondary text-sm disabled:opacity-50"
                            >
                                Baixar QR
                            </button>
                            <button
                                type="button"
                                onClick={printArQr}
                                disabled={!arQrCodeDataUrl}
                                className="btn-primary text-sm disabled:opacity-50"
                            >
                                Imprimir QR
                            </button>
                        </div>
                    </div>
                    {filteredCategories.slice(0, 3).map((category) => (
                        <div key={category.id} className="space-y-2">
                            <h4 className="text-sm font-semibold text-primary">{category.name}</h4>
                            {(category.products || []).slice(0, 3).map((product: any) => (
                                <div key={product.id} className="rounded-xl border border-[#2A2A2A] bg-[#111] p-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <p className="text-sm font-semibold text-white">{product.name}</p>
                                            <p className="text-xs text-muted-foreground">{product.description || 'Sem descrição'}</p>
                                        </div>
                                        <span className="text-sm font-bold text-primary">{formatPrice(getProductDisplayPrice(product))}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </Modal>
        </div>
    );
};

export default AdminMenu;
